














































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

import { Entities, FeedTank, FeedTankType, FeedTypeLevelMetricEnum } from '@/api';
import { numberInputValid } from '@/common/Validators';
import { FeedProductModals, FeedType } from '../../../../models';
import Toast from '@/components/display/toast.vue';
import { FeedProductService } from '../../../../services';
import { FormSelectOption } from '@/models/form-select-option';
import SaveCancelButtons from '@/components/elements/buttons/save-cancel-buttons.vue';
import CustomFormulaConfiguration from '@/components/form pickers/CustomFormulaConfiguration.vue';
import { FormulaInput } from '@/modules/formulas/models/formula-input';
import { FormulasService } from '@/modules/formulas/formulas-service';
import { FuelTankVolumeCalculator } from '@/typings/fuel-volume-calculator';

@Component({
	components: { CustomFormulaConfiguration, SaveCancelButtons },
})
export default class ProvisionFeedTankModal extends Vue {
	@Prop({ required: true, type: Object })
	public feedTank!: FeedTank & { feedtype: FeedType };

	@Prop({ required: false, type: Boolean, default: false })
	public provisionedPreviously!: boolean;

	@Prop({ required: true, type: Function })
	private onProvision!: (feedTank: FeedTank) => Promise<void>;

	@Watch('feedTank')
	public onFeedTankChanged(): void {
		this.resetValues();
	}

	public get nameValid(): boolean {
		return this.name !== null && this.name.trim().length > 0;
	}

	public nominalValid(): boolean | null {
		if (!this.submitted) {
			return null;
		}
		if (!this.isVolume()) return true;
		return numberInputValid({
			field: this.nominalVolume,
			min: 1,
			max: null,
		});
	}

	public nominalWeightValid(): boolean | null {
		if (!this.submitted) {
			return null;
		}
		if (this.isVolume()) return true;
		return numberInputValid({
			field: this.nominalWeight,
			min: 1,
			max: null,
		});
	}

	public get numTanksValid(): boolean | null {
		if (!this.submitted) {
			return null;
		}
		return numberInputValid({
			field: this.numTanks,
			min: 1,
			max: 10,
		});
	}

	public get feedTankTypes(): FormSelectOption[] {
		return [
			{ value: FeedTankType.Silo, text: 'Silo' },
			{ value: FeedTankType.Tank, text: 'Tank' },
			{ value: FeedTankType.Vat, text: 'Vat' },
			{ value: FeedTankType.Bin, text: 'Bin' },
			{ value: FeedTankType.Null, text: 'Unknown' },
		];
	}

	private get formulaOptions() {
		return this.formulasService.formulaOptions;
	}

	public name: string | null = this.feedTank.name ?? null;
	public nominalVolume: number | null = this.feedTank.nominalVolume ?? null;
	public nominalWeight: number | null = this.feedTank.nominalWeight ?? null;
	public numTanks: number = this.feedTank.numTanks ?? 1;
	public feedTypeId: number = this.feedTank.feedTypeId ?? 1;
	public feedTankType: FeedTankType = this.feedTank.type ?? FeedTankType.Silo;
	public modalId: string = FeedProductModals.PROVISION;
	public loading: boolean = true;
	public submitted: boolean = false;
	public feedTypes: FormSelectOption[] | null = null;
	private rawFeedTypes: FeedType[] | undefined;

	private formulaInputs: FormulaInput[] = [];
	private calculatingTank = false;
	private calculation: any = {};
	private parameters: Record<string, any> = {};

	private readonly formulasService = new FormulasService(Entities.FeedTanks);
	private readonly toast: Toast = new Toast();

	public async created(): Promise<void> {
		this.rawFeedTypes = await this.getFeedTypes();
		if (this.rawFeedTypes) {
			this.feedTypes = this.rawFeedTypes.map((feedType: FeedType) => ({
				value: feedType.id,
				text: feedType.name,
			}));
		}

		await this.getFormulas();

		this.loading = false;
	}

	@Watch('feedTank')
	private async getFormulas() {
		await this.formulasService.initalise();
		if (this.feedTank.formulaId) {
			this.formulaInputs = this.formulasService.getFormulaInputs(this.feedTank.formulaId) ?? [];
			if (this.feedTank.formulaParameters) {
				const parameters: Record<string, any> = this.feedTank.formulaParameters;
				this.formulaInputs.forEach((input: FormulaInput) => {
					input.value = parameters[input.name];
				});
			}
		}
		await this.calculateTankVolume();
	}

	public async provisionTank(): Promise<void> {
		this.submitted = true;
		if (
			this.name === null ||
			this.name.trim().length === 0 ||
			(!this.nominalVolume?.toString()?.length && this.isVolume()) ||
			(!this.nominalWeight?.toString()?.length && !this.isVolume()) ||
			this.numTanksValid === false
		) {
			return;
		}

		this.formulaInputs
			.filter((input: FormulaInput) => input.value != null)
			.forEach((input: FormulaInput) => (input.valid = true));
		const emptyFormulaInputs = this.formulaInputs.filter(
			(input: FormulaInput) => input.value == null,
		);
		if (emptyFormulaInputs.length > 0) {
			emptyFormulaInputs.forEach((input: FormulaInput) => (input.valid = false));
			return;
		}

		this.feedTank.name = this.name;
		this.feedTank.nominalVolume = this.nominalVolume ?? undefined;
		this.feedTank.nominalWeight = this.nominalWeight ?? undefined;
		this.feedTank.numTanks = this.numTanks;
		this.feedTank.feedtype = <FeedType>this.rawFeedType() ?? null;
		this.feedTank.feedTypeId = this.feedTypeId;
		this.feedTank.type = this.feedTankType;

		this.feedTank.formulaParameters = {};
		this.formulaInputs.forEach(
			i => ((this.feedTank.formulaParameters as Record<string, any>)[i.name] = i.value),
		);

		await this.onProvision(this.feedTank);
	}

	public dismiss(): void {
		this.$bvModal.hide(this.modalId);
	}

	public resetValues(): void {
		this.name = this.feedTank.name ?? null;
		this.nominalVolume = this.feedTank.nominalVolume ?? null;
		this.nominalWeight = this.feedTank.nominalWeight ?? null;
		this.numTanks = this.feedTank.numTanks ?? 1;
		this.feedTypeId = this.feedTank.feedTypeId ?? 1;
	}

	private async getFeedTypes(): Promise<FeedType[] | undefined> {
		const feedTypesResponse = await new FeedProductService().getFeedTypes();
		if (feedTypesResponse.errorMessage) {
			this.toast.makeToast(feedTypesResponse.errorMessage!, 'Error', 'danger');
			return;
		}
		return feedTypesResponse.feedTypes ?? [];
	}

	private onFormulaChanged(formulaId: number): void {
		this.feedTank.formulaId = formulaId;
		this.formulaInputs = this.formulasService.getFormulaInputs(this.feedTank.formulaId) ?? [];
		this.calculateTankVolume();
	}

	private onFormulaParametersChanged(inputs: FormulaInput[]): void {
		this.formulaInputs = inputs;
		this.calculateTankVolume();
	}

	private async calculateTankVolume() {
		if (!this.feedTank?.id) {
			this.toast.makeToast('No feed tank ID', 'Error Calculating Volume', 'danger');
			return;
		}

		this.calculatingTank = true;
		this.formulaInputs.forEach(i => (this.parameters[i.name] = i.value));
		try {
			if (this.feedTank.formulaId) {
				const formula = this.formulasService.formulas?.find(f => f.id === this.feedTank.formulaId);
				if (!formula) {
					this.calculatingTank = false;
					return;
				}
				this.calculation = await FuelTankVolumeCalculator.calculate(
					9999999, // mod
					formula.formula ?? '',
					formula.name,
					this.feedTank.id,
					this.parameters,
					{ width: 0, height: 0, length: 0, offset: 0 },
					9999999,
					9999999,
					9999999,
					9999999,
				);
			}
		} catch (e) {
			console.log(e);
		}
		this.calculatingTank = false;
	}

	private calculatedNominalDifferent(): boolean {
		return (
			Math.abs((this.nominalVolume ?? 0) - this.calculation.maxVolume) >
			this.calculation.maxVolume * 0.1
		);
	}

	private calculatedNominalWeightDifferent(): boolean {
		const maxVolumeInTonnes = this.calculation.maxVolume * this.rawFeedTypeDensity();
		return Math.abs((this.nominalWeight ?? 0) - maxVolumeInTonnes) > maxVolumeInTonnes * 0.1;
	}

	public isVolume(): boolean {
		return this.rawFeedType()?.levelMetric === FeedTypeLevelMetricEnum.Volume;
	}

	public rawFeedTypeDensity(): number {
		return this.rawFeedType()?.density ?? 1;
	}

	public rawFeedType(): FeedType | null {
		return this.rawFeedTypes?.find(x => x.id === this.feedTypeId) ?? null;
	}
}
